import { Add } from "@mui/icons-material";
import { Alert, Box, Button } from "@mui/material";
import { useCallback, useMemo, useState } from "react";

import { DjangoPaginationAdapter } from "api/common/adapters/pagination.adapters";
import { TableQueryParams } from "api/common/adapters/pagination.types";
import { createQueryAdapter } from "api/common/adapters/queryParam.adapter";
import {
  ACCESS_LEVELS,
  AUTH_ROLES,
  PERMISSION_AREAS,
} from "api/common/types/auth.types";
import { useListProductsQuery } from "api/memberPortal/product/product.api";
import { Product } from "api/memberPortal/project/product/product.types";
import ManageEvalTeamModal from "components/Dashboard/ProductsModule/ManageEvalTeamModal";
import FilterDisplayButton from "components/Filters/FilterDisplayButton";
import { useAppForm } from "components/Forms/AppForm";
import { useAuth } from "hooks/useAuth";
import { usePageTitle } from "hooks/usePageTitle";
import { checkPermissions } from "utils/permissions";

import ProductForm from "../ProductForm/ProductFormRewrite/ProductForm.modal";

import PineFilter from "./components/filter/Pine.filter";
import PineTable from "./components/ProductsInEvalTable/Pine.table";
import initialPineFilters from "./components/ProductsInEvalTable/schemas/initialPineFilter";

const pineApiAdapter = createQueryAdapter({
  paginationAdapter: DjangoPaginationAdapter,
});

const DRAWER_WIDTH = "350px";
const BREADCRUMBS_HEIGHT = "40px";
const JUMP_VID_HEIGHT = "58px";
const HIDE_FILTER_BUTTON_HEIGHT = "53px";

const ProductsOverview = () => {
  const [currentProduct, setCurrentProduct] = useState<Product | null>(null);
  const [showProductModal, setShowProductModal] = useState(false);

  const [alertInfo, setAlertInfo] = useState({ message: "", show: false });
  const [isSwitch, setIsSwitch] = useState(false);
  const [filterOpen, setFilterOpen] = useState<boolean>(true);
  const [showManageEvalTeamModal, setShowManageEvalTeamModal] = useState(false);
  const { permissions } = useAuth();

  // Can we extract these into a hook or reusable component somehow?
  const [activeFilters, setActiveFilters] = useState(initialPineFilters);
  const [tableParams, setTableParams] = useState<TableQueryParams>({
    page: 0,
    pageSize: 50,
    sort: { field: "v_id", direction: "desc" },
    search: "",
  });

  const pineQueryParams = useMemo(() => {
    return pineApiAdapter.toTableParams({
      filters: activeFilters,
      pagination: tableParams,
    });
  }, [activeFilters, tableParams]);

  const { data: products, isFetching: isFetchingProducts } =
    useListProductsQuery(pineQueryParams);

  const productFilter = useAppForm({
    defaultValues: initialPineFilters,
    onSubmit: ({ value }) => {
      setActiveFilters(value);
      setTableParams((prev) => ({ ...prev, page: 0 }));
    },
  });

  const handleTableParamsChange = useCallback(
    (queryParams: TableQueryParams) => {
      setTableParams(queryParams);
    },
    [],
  );

  const handleOpenProductModal = () => {
    setCurrentProduct(null);
    setShowProductModal(true);
  };

  const handleCloseProductModal = () => {
    setShowProductModal(false);
    setCurrentProduct(null);
  };

  const switchForms = (product: Product) => {
    setCurrentProduct(product);
    setIsSwitch(true);
    setShowProductModal(false);
    setShowManageEvalTeamModal(true);
  };

  const switchToProduct = () => {
    setIsSwitch(false);
    setShowProductModal(true);
  };

  usePageTitle("NIAP Community - Products Table");

  const showArchivedForm = Boolean(
    activeFilters.status?.includes("Archived") ||
    activeFilters.status?.length === 0,
  );

  const canAddNewProjects = checkPermissions({
    permissions,
    area: PERMISSION_AREAS.PROJECT,
    access: ACCESS_LEVELS.CREATE,
    role: AUTH_ROLES.LAB,
  });

  return (
    <>
      {alertInfo.show && (
        <Alert
          severity="success"
          onClose={() => setAlertInfo({ message: "", show: false })}
        >
          {alertInfo.message}
        </Alert>
      )}

      <Box display="flex" justifyContent="flex-end" gap={2}>
        {canAddNewProjects && (
          <Button
            startIcon={<Add />}
            variant="contained"
            onClick={handleOpenProductModal}
          >
            New Product
          </Button>
        )}

        <FilterDisplayButton
          side="right"
          onClick={() => setFilterOpen(!filterOpen)}
          show={filterOpen}
        />
      </Box>

      {/* TODO: LOOK AT TURNING THIS PATTERN INTO A FILTER TABLE LAYOUT COMPONENT */}
      <Box
        display="flex"
        width="100%"
        maxHeight={`calc(100% - ${BREADCRUMBS_HEIGHT} - ${JUMP_VID_HEIGHT} - ${HIDE_FILTER_BUTTON_HEIGHT} - 1em)`}
        height="100%"
        columnGap={1}
      >
        <PineTable
          products={products}
          isFetching={isFetchingProducts || !products}
          drawerWidth={DRAWER_WIDTH}
          filterOpen={filterOpen}
          showArchivedForm={showArchivedForm}
          tableParams={tableParams}
          onTableParamsChange={handleTableParamsChange}
        />

        <PineFilter
          form={productFilter}
          open={filterOpen}
          width={DRAWER_WIDTH}
        />
      </Box>

      {showProductModal && (
        <ProductForm
          show={showProductModal}
          onClose={handleCloseProductModal}
          product_id={currentProduct?.product_id}
          switchForms={switchForms}
          onShowAlertAndRefetch={(message) =>
            setAlertInfo({ message, show: true })
          }
        />
      )}

      <ManageEvalTeamModal
        show={showManageEvalTeamModal}
        setShow={setShowManageEvalTeamModal}
        currentProductInEval={currentProduct}
        switchForms={switchToProduct}
        isSwitch={isSwitch}
        // TODO: refactor out of underlying form
        // there are too many use sites to refactor now
        setReloadFormData={null}
      />
    </>
  );
};

export default ProductsOverview;
